import { isNil, orderBy } from 'lodash';

import numOrDefault from 'lib/helpers/numOrDefault';
import { ia } from 'lib/helpers/utility';

import { amountToCents } from 'components/practice/BeyondBilling/Claims/CMS1500/lib/amountToCents';
import { calcLatestBenefits } from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/calcLatestBenefits';
import {
  modifyPackages,
  modifyProducts
} from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/internal/configs';
import {
  denied_codes,
  patient_responsibility
} from 'components/practice/BeyondBilling/lib/insuranceAdjustmentGroups';

const primaryCalculation = (procedure) => {
  let charge = 0;
  let ins_payment = 0;
  let ins_adjustment = 0;
  let pt_adjustment = 0;
  const other_adj = procedure?.other_adj || 0;
  const ins_types = ['primary', 'secondary', 'tertiary'];

  charge += ['self_pay', 'cash'].includes(procedure.type)
    ? (procedure.pt_balance ?? +procedure?.total_cost_cents ?? 0)
    : procedure.charge || 0;

  ins_types?.forEach((insType) => {
    const adjustments = procedure?.[insType];
    if (adjustments) {
      const insTypeLength = adjustments?.length - 1 || 0;
      const lastAdjustment = adjustments[insTypeLength];

      ins_payment += Math.round(lastAdjustment?.paid || 0) || 0;

      if (ia(lastAdjustment?.pt_adj)) {
        lastAdjustment?.pt_adj?.forEach((adj) => {
          const { group, code, amount } = adj;

          if (lastAdjustment?.patient_responsibility === false) {
            if (!(group === 'PR' && +code === 45)) {
              pt_adjustment += amountToCents(amount);
            }
          } else {
            pt_adjustment += amountToCents(amount);
          }
        });
      }

      if (ia(lastAdjustment?.ins_adj)) {
        lastAdjustment?.ins_adj?.forEach((adj) => {
          const { group, code, amount } = adj;

          const hasPRCode = patient_responsibility[group]?.includes(+code);
          const hasDeniedCode = denied_codes[group]?.includes(code);

          if (lastAdjustment?.patient_responsibility === false) {
            if (!(group === 'PR' && +code === 45)) {
              if (group === 'PR' || hasPRCode || hasDeniedCode) {
                pt_adjustment += amountToCents(amount);
              }
            }
          } else {
            if (group === 'PR' || hasPRCode || hasDeniedCode) {
              pt_adjustment += amountToCents(amount);
            }
          }
        });
      }
    }
  });

  let insAdj =
    numOrDefault(charge) -
    numOrDefault(ins_payment) -
    numOrDefault(calcLatestBenefits({ procedure })?.ptBalance);

  if (['product', 'package', 'self_pay'].includes(procedure.type || 0) || procedure?.denied) {
    insAdj = 0;
  }

  ins_adjustment += insAdj;
  return { charge, ins_adjustment, pt_adjustment, ins_payment, other_adj };
};

const secondCalculation = (procedure) => {
  const { ins_payment = 0, pt_balance = 0 } = procedure;
  const final_ins_payment = ins_payment - pt_balance;

  return Math.max(final_ins_payment, 0);
};

export function calculateProceduresTotal(procedures) {
  let final_charge = 0;
  let final_ins_payment = 0;
  let final_ins_adjustment = 0;
  let final_pt_adjustment = 0;
  let final_other_adjustment = 0;
  let final_qty = 0;

  procedures?.forEach?.((procedure) => {
    const qty = (procedure?.quantity ?? procedure?.units ?? procedure?.sales_count) || 1;

    final_qty += qty;

    const qtyToMultiply =
      !procedure?.type || procedure?.type?.toLowerCase() === 'product' ? 1 : qty;

    if (Object?.keys?.(procedure).includes('primary')) {
      const {
        charge = 0,
        ins_adjustment = 0,
        pt_adjustment = 0,
        ins_payment = 0,
        other_adj
      } = primaryCalculation(procedure);

      final_charge += numOrDefault(charge * qtyToMultiply);
      final_ins_adjustment += numOrDefault(ins_adjustment * qtyToMultiply);
      final_ins_payment += numOrDefault(ins_payment * qtyToMultiply);
      final_pt_adjustment += numOrDefault(pt_adjustment);
      final_other_adjustment += numOrDefault(other_adj);
    } else if (
      procedure?.ins_payment > 0 &&
      procedure?.ins_adjustment > 0 &&
      procedure?.charge === procedure?.ins_payment + procedure?.ins_adjustment
    ) {
      const {
        charge = 0,
        pt_adjustment = 0,
        ins_adjustment = 0,
        pt_balance,
        other_adj = 0
      } = procedure;

      const ins_payment = secondCalculation(procedure);

      final_charge += (charge || pt_balance || 0) * qtyToMultiply;
      final_ins_adjustment += numOrDefault(ins_adjustment * qtyToMultiply);
      final_ins_payment += numOrDefault(ins_payment * qtyToMultiply);
      final_pt_adjustment += numOrDefault(pt_adjustment);
      final_other_adjustment += numOrDefault(other_adj);
    } else {
      if (procedure?.status !== 'cancelled') {
        let {
          charge = 0,
          total_amount_cents = 0,
          pt_balance,
          total_cost_cents = 0,
          charge_type,
          ins_payment,
          ins_adjustment,
          other_adj = 0,
          type,
          denied = false
        } = procedure || {};

        let insAdj = 0;

        if (type !== 'primary') {
          insAdj = Math.max(charge - pt_balance, 0);
        }

        if (['product', 'package', 'self_pay', 'cash', 'unapplied'].includes(type) || denied) {
          insAdj = 0;
          charge = 0;
        }

        if (
          isNil(pt_balance) &&
          (charge_type == 'copay' || charge_type == 'primary') &&
          isNil(ins_payment) &&
          isNil(ins_adjustment)
        ) {
          insAdj = charge;
        }

        final_charge +=
          ((+charge || pt_balance) ?? (+total_cost_cents || +total_amount_cents)) * qtyToMultiply;
        final_ins_adjustment += numOrDefault(insAdj * qtyToMultiply);
        final_pt_adjustment += numOrDefault(pt_balance);
        final_other_adjustment += numOrDefault(other_adj);
      }
    }
  });

  return {
    charge: final_charge,
    ins_adjustment: final_ins_adjustment,
    pt_adjustment: final_pt_adjustment,
    ins_payment: final_ins_payment,
    other_adj: final_other_adjustment,
    qty: final_qty
  };
}

export const getProceduresTotal = (invoices) => {
  const services = [];
  const procedures = invoices.map((invoice) => invoice.procedures || []).flat();

  if (procedures?.length) {
    services.push(...procedures);
  }
  const products = invoices
    .map((invoice) => modifyProducts({ products: invoice?.products || [], isTotal: true }))
    .flat();
  if (products?.length) {
    services.push(...products);
  }
  const packages = invoices
    .map((invoice) => modifyPackages({ packages: invoice?.packages || [], isTotal: true }))
    .flat();
  if (packages?.length) {
    services.push(...packages);
  }

  const { amount_paid, prevDiscount, tax, write_off, ptBalance, totalAmount } = invoices.reduce(
    (acc, invoice) => {
      acc.amount_paid += invoice?.amount_paid || 0;
      acc.prevDiscount += invoice?.discount || 0;
      acc.tax += invoice?.tax || 0;
      acc.write_off += invoice?.write_off || 0;
      acc.ptBalance += invoice?.invoice_balance || invoice?.balance || 0;
      acc.totalAmount += invoice?.total_amount || 0;

      return acc;
    },
    { amount_paid: 0, prevDiscount: 0, tax: 0, write_off: 0, ptBalance: 0, totalAmount: 0 }
  );

  const calculatedProcedures = calculateProceduresTotal(services);

  return {
    ...calculatedProcedures,
    amount_paid,
    prevDiscount,
    tax,
    write_off,
    ptBalance,
    totalAmount
  };
};

export const getInvoicesDetails = (invoices) => {
  const details = orderBy(invoices, ['dateofservice'], 'desc').map((invoice) => {
    return {
      id: invoice.id,
      dateOfService: invoice.dateofservice,
      discount: invoice?.discount,
      procedures: invoice.procedures,
      products: invoice.products,
      packages: invoice.packages,
      tax: invoice.tax || 0,
      amount_paid: invoice.amount_paid || 0,
      write_off: invoice.write_off || 0,
      created_at: invoice.created_at
    };
  });
  return details;
};
